import GlobalDataContext from "@components/Layout/GlobalDataContext"
import ProductSlider from "@components/ProductSlider"
import breakpoints from "@constants/breakpoints"
import { SELECTED_PRODUCTS_KEY } from "@constants/storageKeys"
import useElevarEvents, { ElevarProductProps } from "@hooks/useElevarEvents"
import useEnhancedEcommerceEvents, {
  GTMProductProps,
} from "@hooks/useEnhancedEcommerceEvents"
import useMultiWarehouseInventory, {
  INVENTORY_ITEM_TRACKED,
} from "@hooks/useMultiWarehouseInventory"
import useScrollHandler from "@hooks/useScrollHandler"
import { Dispatch, RootState } from "@redux/store"
import getAvailabilityByRegion from "@utils/getAvailabilityByRegion"
import { formatContentfulVariant, getIn } from "@utils/index"
import { getLocalStorage, writeLocalStorage } from "@utils/storage"
import R from "ramda"
import React from "react"
import { connect } from "react-redux"
import styled from "styled-components"

import { ContentfulContentBaseProps } from "../types"

const Container = styled.div`
  padding-top: 32px;

  @media ${breakpoints.laptop} {
    padding-top: calc(0.5 / 12 * 100vw);
  }
`

interface ContentfulProductSliderProps extends ContentfulContentBaseProps {}

const ContentfulProductSlider: React.FC<ContentfulProductSliderProps> = ({
  addToCart,
  blockId,
  customerLocation,
  customerLocationFetched,
  products,
  title,
}) => {
  if (!products || products.length === 0) {
    return null
  }

  const [showForm, setShowForm] = React.useState<boolean>(false)

  React.useEffect(() => {
    if (
      customerLocationFetched &&
      !!customerLocation?.customerCountryCode &&
      !showForm
    ) {
      setShowForm(true)
    }
  }, [customerLocationFetched, customerLocation])

  const standardizedProducts = React.useMemo(() => {
    return products
      .filter(
        (product) =>
          product.__typename === "ContentfulProductVariant" ||
          (product.__typename === "ContentfulProduct" &&
            product.variants?.length > 0)
      )
      .map((data: any) => {
        if (data.__typename === "ContentfulProductVariant") {
          const productData = getIn(data, "product.0", {})
          const variantData = R.omit(["product"], data)

          return {
            ...productData,
            __typename: data.__typename,
            variants: [formatContentfulVariant(variantData)],
          }
        }

        return {
          ...data,
          variants: data.variants.map(formatContentfulVariant),
        }
      })
  }, [products])

  const allVariantSkus = React.useMemo(() => {
    const allVariants = standardizedProducts.reduce((acc, product) => {
      const validVariants = product.variants.filter((variant: any) => {
        if (product.customPrint) {
          return (
            variant.deviceSlug &&
            variant.deviceName &&
            variant.images?.length > 0
          )
        }
        return variant.images?.length > 0
      })
      return [...acc, ...validVariants]
    }, [])
    const allVariantSkus = allVariants.map(({ sku }) => sku)

    return allVariantSkus
  }, [standardizedProducts])

  const { inventoryData } = useMultiWarehouseInventory(allVariantSkus)

  const { countryProhibitedProducts }: any = React.useContext(GlobalDataContext)

  const nonprohibitedProducts = React.useMemo(() => {
    if (
      customerLocationFetched &&
      customerLocation?.customerCountryCode &&
      countryProhibitedProducts &&
      countryProhibitedProducts.length > 0
    ) {
      return standardizedProducts.filter((product) => {
        if (
          customerLocation.customerCountryCode !== "US" &&
          product.customPrint
        ) {
          return false
        }

        countryProhibitedProducts.forEach((rule) => {
          const countries = getIn(rule, "countries", [])
          const products = getIn(rule, "products", [])

          if (countries.includes(customerLocation.customerCountryCode)) {
            const prohibitedProduct = products.find(
              ({ slug }) => slug === product.slug
            )

            if (prohibitedProduct) {
              return false
            }
          }
        })

        return true
      })
    }

    return standardizedProducts
  }, [
    customerLocationFetched,
    customerLocation,
    countryProhibitedProducts,
    standardizedProducts,
  ])

  const availableProducts = React.useMemo(() => {
    if (
      customerLocationFetched &&
      customerLocation?.customerCountryCode &&
      inventoryData
    ) {
      const productsWithInventory = nonprohibitedProducts.map((product) => {
        const variantsWithInventory: any = []

        product.variants.forEach((variant) => {
          const tracked = getIn(
            inventoryData,
            `${variant.sku}.${INVENTORY_ITEM_TRACKED}`,
            true
          )
          const isAvailable = getAvailabilityByRegion(
            customerLocation.customerCountryCode,
            variant.sku,
            inventoryData
          ).isAvailable

          if (tracked && isAvailable) {
            variantsWithInventory.push(variant)
          }
        })

        return {
          ...product,
          variants: variantsWithInventory,
        }
      })

      return productsWithInventory.filter(
        (product) => product.variants.length > 0
      )
    }

    return nonprohibitedProducts
  }, [
    customerLocationFetched,
    customerLocation,
    inventoryData,
    nonprohibitedProducts,
  ])

  const { onAddProductToCart, onClickProductCard, onViewProductList } =
    useEnhancedEcommerceEvents()
  const { elevarOnAddToCart, elevarOnSelectItem, elevarOnViewItemList } =
    useElevarEvents()

  const formattedProducts = React.useMemo(() => {
    return availableProducts.map((product: any, i) => {
      const shopifyProductId = getIn(
        product,
        "shopifyProduct.shopifyProductId",
        ""
      )

      let multipleDevices
      if (product.devices) {
        multipleDevices = product.devices.reduce((acc, curr) => {
          if (acc) {
            acc = acc + `, ${curr.name}`
          } else {
            acc = curr.name
          }

          return acc
        }, undefined)
      }

      let list = ""

      if (typeof window !== "undefined") {
        list = getIn(window, "location.pathname")
      }

      const gtmProductView: GTMProductProps = {
        brand: product.brand.name,
        device: multipleDevices ? multipleDevices : product.device.name,
        deviceType: product.device.type.name,
        list: `Product Slider: ${title}`,
        price: product.variants[0].price,
        productId: shopifyProductId,
        productName: product.productName,
        sku: product.variants[0].sku,
        variantName: product.variants[0].name,
      }

      const elevarProductView: ElevarProductProps = {
        brand: "Urban Armor Gear",
        device: multipleDevices ? multipleDevices : product.device.name,
        deviceType: product.device.type.name,
        list,
        position: i + 1,
        price: product.variants[0].price,
        productId: shopifyProductId,
        productName: product.productName,
        sku: product.variants[0].sku,
        variantId: product.variants[0].variantId,
        variantName: product.variants[0].name,
      }

      const variantsWithClickHandlers = product.variants.map((variant) => {
        const imageSrc = getIn(variant, "images.0", "")
        const [cleanSrc] = imageSrc.split("?")
        const formattedImageSrc = `https:${cleanSrc}`

        const onClick = () => {
          const gtmProductClick: GTMProductProps = {
            brand: product.brand.name,
            device:
              variant.deviceName ||
              (multipleDevices
                ? multipleDevices.replace("#", ", ")
                : product.device?.name),
            deviceType: product.device.type.name,
            list: `Product Slider: ${title}`,
            price: variant.price,
            productId: shopifyProductId,
            productName: product.productName,
            sku: variant.sku,
            variantName: variant.name,
          }

          const elevarProductClick: ElevarProductProps = {
            brand: "Urban Armor Gear",
            device:
              variant.deviceName ||
              (multipleDevices
                ? multipleDevices.replace("#", ", ")
                : product.device?.name),
            deviceType: product.device.type.name,
            list,
            position: i + 1,
            price: variant.price,
            productId: shopifyProductId,
            productName: product.productName,
            sku: variant.sku,
            variantId: variant.variantId,
            variantName: variant.name,
          }

          const selectedProducts = getLocalStorage(SELECTED_PRODUCTS_KEY) || {}
          writeLocalStorage(SELECTED_PRODUCTS_KEY, {
            ...selectedProducts,
            [elevarProductClick.productId]: elevarProductClick.list,
          })

          onClickProductCard(gtmProductClick)
          elevarOnSelectItem(elevarProductClick, elevarProductClick.list!)
        }

        const onAddToCart = (quantity) => {
          const cartProduct = {
            brand: product.brand?.name,
            builtInMagnet: !!product.builtInMagnet,
            bundleProduct: product.bundleProduct,
            color: variant.color,
            compareAtPrice: variant.compareAtPrice,
            contentfulProductId: product.contentful_id,
            contentfulVariantId: variant.contentfulVariantId,
            customPrint: product.customPrint,
            device:
              variant.deviceName ||
              (multipleDevices
                ? multipleDevices.replace("#", ", ")
                : product.device?.name),
            deviceType: product.device?.type?.name,
            image: formattedImageSrc,
            language: product.language,
            marketPrices: variant.marketPrices,
            price: variant.price,
            productName: product.productName,
            productSlug: product.slug,
            productSubType: product.productSubType?.slug,
            quantity,
            shopifyProductId: product.shopifyProduct?.shopifyProductId,
            shopifyVariantId: variant.variantId,
            size: variant.size,
            sku: variant.sku,
            variantDevice: variant.deviceName,
            variantName: variant.name,
          }

          const gtmProductAddToCart: GTMProductProps = {
            brand: product.brand.name,
            device:
              variant.deviceName ||
              (multipleDevices
                ? multipleDevices.replace("#", ", ")
                : product.device?.name),
            deviceType: product.device?.type?.name,
            price: variant.price,
            productId: product.shopifyProduct?.shopifyProductId,
            productName: product.productName,
            quantity,
            sku: variant.sku,
            variantName: variant.name,
          }

          const elevarProductAddToCart: ElevarProductProps = {
            brand: "Urban Armor Gear",
            device:
              variant.deviceName ||
              (multipleDevices
                ? multipleDevices.replace("#", ", ")
                : product.device?.name),
            deviceType: product.device?.type?.name,
            image: formattedImageSrc,
            list: `Product Slider: ${title}`,
            price: variant.price,
            productId: product.shopifyProduct?.shopifyProductId,
            productName: product.productName,
            quantity,
            sku: variant.sku,
            variantId: variant.variantId,
            variantName: variant.name,
          }

          addToCart({ cartProduct })

          onAddProductToCart(gtmProductAddToCart)

          elevarOnAddToCart(elevarProductAddToCart, `Product Slider: ${title}`)
        }

        return {
          ...variant,
          onAddToCart,
          onClick,
        }
      })

      return {
        ...product,
        elevarProductView,
        gtmProductView,
        variants: variantsWithClickHandlers,
      }
    })
  }, [availableProducts])

  const [onViewProductListCalled, setOnViewProductListCalled] =
    React.useState<boolean>(false)

  const containerRef = React.useRef<HTMLDivElement>(null)

  const onScroll = () => {
    if (
      !onViewProductListCalled &&
      typeof window !== "undefined" &&
      containerRef &&
      containerRef.current &&
      formattedProducts[0].variants[0].price
    ) {
      if (
        containerRef.current.getBoundingClientRect().top < window.innerHeight
      ) {
        const gtmProducts = formattedProducts.map(
          ({ gtmProductView }) => gtmProductView
        )
        const elevarProducts = formattedProducts.map(
          ({ elevarProductView }) => elevarProductView
        )
        onViewProductList(gtmProducts)
        elevarOnViewItemList(elevarProducts)
        setOnViewProductListCalled(true)
      }
    }
  }

  useScrollHandler({
    onScroll,
    runForProps: [containerRef, formattedProducts, onViewProductListCalled],
  })

  return (
    <Container id={blockId || undefined} ref={containerRef}>
      <ProductSlider products={formattedProducts} title={title} />
    </Container>
  )
}

const mapState = (state: RootState) => ({
  customerLocation: state.customer.customerLocation,
  customerLocationFetched: state.customer.customerLocationFetched,
})

const mapDispatch = (dispatch: Dispatch) => ({
  addToCart: dispatch.checkout.addToCart,
})

export default connect(mapState, mapDispatch)(ContentfulProductSlider)
